import { useCallback, useMemo } from 'react'
import { StyleSheet, ViewStyle } from 'react-native'

import { MotiView } from 'moti'
import { Extrapolation } from 'react-native-reanimated'

import { Stack } from '../layout'
import Pressable from '../pressable'
import { colors } from '../tailwind'

const width = 40
const height = 20
const thumbHeight = 14
const thumbWidth = 14
const thumbOffset = 4

export type SwitchProps = {
  accessibilityLabel?: string
  checked?: boolean
  shape?: 'circle' | 'square'
  onChange?: (nextValue: boolean) => void
  intent?: 'primary' | 'warning' | 'error' | 'success'
  type?: 'normal' | 'ghosted'
}

export const Switch = (props: SwitchProps) => {
  const {
    checked,
    onChange,
    accessibilityLabel,
    shape = 'circle',
    intent = 'primary',
    type = 'normal',
  } = props

  const variantStyles = useMemo(() => {
    let variantStyles: { wrapper: ViewStyle; thumb: ViewStyle }
    variantStyles = {
      wrapper: {
        backgroundColor: checked ? colors[`${intent}`][10] : colors.grays[30],
        borderColor: checked ? colors[`${intent}`][100] : colors.grays[40],
        borderWidth: 1,
      },
      thumb: {
        backgroundColor: checked ? colors[`${intent}`][100] : colors.grays[50],
      },
    }
    if (shape === 'square') {
      variantStyles = {
        ...variantStyles,
        wrapper: { ...variantStyles.wrapper, borderRadius: 8 },
        thumb: { ...variantStyles.thumb, borderRadius: 6 },
      }
    } else {
      variantStyles = {
        ...variantStyles,
        wrapper: { ...variantStyles.wrapper, borderRadius: 999 },
        thumb: { ...variantStyles.thumb, borderRadius: 999 },
      }
    }
    if (type === 'ghosted') {
      variantStyles = {
        ...variantStyles,
        wrapper: {
          ...variantStyles.wrapper,
          backgroundColor: colors[`${intent}`][10],
        },
      }
    } else {
      variantStyles = {
        ...variantStyles,
        wrapper: {
          ...variantStyles.wrapper,
        },
      }
    }
    return variantStyles
  }, [shape, type, intent, checked])

  return (
    <Pressable
      style={[styles.pressableStyle]}
      onPress={useCallback(() => {
        if (onChange) {
          onChange(!checked)
        }
      }, [onChange, checked])}
      accessibilityRole="switch"
      accessibilityState={{ checked }}
      accessibilityLabel={accessibilityLabel}
    >
      <Stack style={[styles.wrapper, { ...variantStyles.wrapper }]}>
        <Stack style={{ width, height }} />
      </Stack>
      <MotiView
        style={[styles.thumbStyle, { ...variantStyles.thumb }]}
        animate={{
          translateX: checked ? width - thumbWidth - thumbOffset : thumbOffset,
        }}
        // @ts-ignore
        transition={{ overshootClamping: Extrapolation.CLAMP }}
      />
    </Pressable>
  )
}

const styles = StyleSheet.create({
  thumbStyle: {
    position: 'absolute',
    height: thumbHeight,
    width: thumbWidth,
    backgroundColor: 'white',
  },
  pressableStyle: {
    justifyContent: 'center',
    width,
  },
  wrapper: { overflow: 'hidden' },
})
